/**
 * <bk-author-books> — інші книги тих самих авторів за індексом каталогу.
 *
 * Чому індекс, а не пошук — див. sections/bk-author-books.liquid. Індекс
 * качаємо одразу, з низьким пріоритетом, щоб не змагався з обкладинкою
 * (LCP). До відповіді на місці карток стоять скелетони тієї ж висоти;
 * картки бере тема через `bkv-catalog-cards`, як і в bkv-catalog.js.
 * Немає книг або щось упало — секцію ховаємо.
 */

/** Має збігатися з `paginate` у bkv-catalog-cards. */
const CARDS_CHUNK = 50;

/** Скільки чекати, поки STOQ ініціалізує сторінку товару. */
const STOQ_WAIT_MS = 10000;

class BkAuthorBooks extends HTMLElement {
  connectedCallback() {
    this.#load().catch((error) => {
      console.error('bk-author-books:', error);
      this.hidden = true;
    });
  }

  async #load() {
    const { indexUrl, cardsUrl, productId, limit } = this.dataset;
    const authors = JSON.parse(this.querySelector('[data-bk-authors]')?.textContent ?? '[]');
    const viewport = this.querySelector('[data-bk-viewport]');
    if (!indexUrl || !cardsUrl || !viewport || !authors.length) {
      this.hidden = true;
      return;
    }

    const response = await fetch(indexUrl, { priority: 'low' });
    if (!response.ok) throw new Error(`index HTTP ${response.status}`);
    const ids = pickIds(await response.json(), authors, Number(productId), Number(limit) || 8);
    const cards = ids.length ? await loadCards(cardsUrl, ids) : [];

    if (!cards.length) {
      this.hidden = true;
      return;
    }

    viewport.innerHTML = cards.map((card) => `<div class="bk-related__item bk-fade-in">${card}</div>`).join('');
    this.removeAttribute('aria-busy');

    if (viewport.querySelector('.bk-card__stoq-placeholder')) {
      renderStoqButtons().catch((error) => console.error('bk-author-books STOQ:', error));
    }
  }
}

/**
 * id книг тих самих авторів: спершу в наявності, далі решта, всередині —
 * порядок індексу (порядок колекції «весь каталог»).
 * @param {any} index
 * @param {string[]} authors
 * @param {number} productId
 * @param {number} limit
 */
function pickIds(index, authors, productId, limit) {
  const { items, dict } = index;
  const wanted = new Set(authors.map((name) => dict.author.indexOf(name)).filter((i) => i >= 0));
  if (!wanted.size) return [];

  const inStock = [];
  const rest = [];
  for (let i = 0; i < items.id.length; i++) {
    if (items.id[i] === productId) continue;
    if (!items.author[i].some((a) => wanted.has(a))) continue;
    (items.available[i] ? inStock : rest).push(items.id[i]);
    if (inStock.length >= limit) break;
  }
  return [...inStock, ...rest].slice(0, limit);
}

/**
 * Розмітка карток у порядку `ids`. Товар, якого немає у відповіді (знятий
 * з публікації після збирання індексу), просто пропускаємо.
 * @param {string} cardsUrl
 * @param {number[]} ids
 */
async function loadCards(cardsUrl, ids) {
  const byId = new Map();
  for (let i = 0; i < ids.length; i += CARDS_CHUNK) {
    const url = new URL(cardsUrl, location.origin);
    url.searchParams.set('q', ids.slice(i, i + CARDS_CHUNK).map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'bkv-catalog-cards');

    const response = await fetch(url);
    if (!response.ok) throw new Error(`cards HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    for (const item of doc.querySelectorAll('li[data-product-id]')) {
      byId.set(Number(/** @type {HTMLElement} */ (item).dataset.productId), item.innerHTML);
    }
  }
  return ids.map((id) => byId.get(id)).filter(Boolean);
}

/**
 * Кнопки підписки STOQ на картках «Немає в наявності».
 *
 * На сторінці товару STOQ вантажить лише restockrocket-product-bis.js — він
 * малює віджет головного товару, а кнопок у картках не знає. Їх малює
 * restockrocket-collection-bis.js, тож підтягуємо його самі: URL застосунок
 * кладе в `_RestockRocketConfig.scriptUrlCollectionBis`, версія в шляху нам
 * не потрібна.
 *
 * Два нюанси, перевірені на живій сторінці:
 *   - обидва скрипти ініціалізуються один раз на прапорці `config.rendered`.
 *     Якщо collection-скрипт прийде першим, він займе прапорець і віджет
 *     головного товару не з'явиться. Тому чекаємо, поки прапорець поставить
 *     product-скрипт, і лише тоді вантажимо свій;
 *   - `renderButtonsForPage()` малює кнопки карток лише для `pageType`
 *     collection / search / index. Тип читається синхронно в момент виклику,
 *     тож підміняємо його на один виклик і одразу повертаємо. Виклик ставить
 *     і власне опитування посилань (раз на 250 мс), тому робимо його один раз.
 */
let stoqStarted = false;

async function renderStoqButtons() {
  if (stoqStarted) return;
  stoqStarted = true;

  const config = /** @type {any} */ (window)._RestockRocketConfig;
  const src = config?.scriptUrlCollectionBis;
  if (!src) return;

  const deadline = Date.now() + STOQ_WAIT_MS;
  while (!config.rendered) {
    if (Date.now() > deadline) return;
    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });

  const render = /** @type {any} */ (window)._RestockRocket?.renderButtonsForPage;
  if (typeof render !== 'function') return;

  const pageType = config.pageType;
  config.pageType = 'collection';
  try {
    render();
  } finally {
    config.pageType = pageType;
  }
}

if (!customElements.get('bk-author-books')) {
  customElements.define('bk-author-books', BkAuthorBooks);
}
